import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type PreviewIncidentReportResponse } from '@roadfootage/api-client'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import type { SearchRouteState } from '../../utils/search-route'

const PAGE_SIZE = 20

export const useSearchStore = defineStore('search', () => {
  const { searchApi } = useApi()

  const params = ref<SearchRouteState | null>(null)
  const results = ref<PreviewIncidentReportResponse[]>([])
  const totalCount = ref(0)
  const page = ref(0)
  const isLoading = ref(false)
  const hasError = ref(false)
  // True once a search for non-blank text has been requested; separates "nothing typed yet" from "no results".
  const hasSearched = ref(false)

  const hasMore = computed(() => results.value.length < totalCount.value)

  // Every request takes a ticket; a response only applies if its ticket is still the latest.
  let latestRequest = 0

  function clear() {
    latestRequest++
    params.value = null
    results.value = []
    totalCount.value = 0
    page.value = 0
    isLoading.value = false
    hasError.value = false
    hasSearched.value = false
  }

  async function fetchPage(state: SearchRouteState, pageNumber: number) {
    const ticket = ++latestRequest
    isLoading.value = true
    hasError.value = false
    try {
      const response = await searchApi.searchSearchIncidents({
        q: state.q,
        hasFootage: state.hasFootage,
        period: state.period,
        page: pageNumber,
        pageSize: PAGE_SIZE
      })
      if (ticket !== latestRequest) return
      const items = response.items ?? []
      results.value = pageNumber === 1 ? [...items] : [...results.value, ...items]
      totalCount.value = response.totalCount ?? 0
      page.value = pageNumber
    } catch (error) {
      if (ticket !== latestRequest) return
      logger.error('search error', { error })
      hasError.value = true
    } finally {
      if (ticket === latestRequest) isLoading.value = false
    }
  }

  async function search(state: SearchRouteState) {
    const q = state.q.trim()
    if (!q) {
      clear()
      return
    }
    logger.debug('search', { queryLength: q.length })
    params.value = { ...state, q }
    results.value = []
    totalCount.value = 0
    page.value = 0
    hasSearched.value = true
    await fetchPage(params.value, 1)
  }

  async function loadMore() {
    if (!params.value || isLoading.value || !hasMore.value) return
    await fetchPage(params.value, page.value + 1)
  }

  return {
    search,
    loadMore,
    clear,
    results,
    totalCount,
    hasMore,
    isLoading,
    hasError,
    hasSearched
  }
})
